import cx from 'clsx';
import React from 'react';

import { useDeviceRatio } from '../../hooks/useDeviceRatio';
import AssetLayer from '../AssetLayer/AssetLayer';
import { LayersWrapper } from '../game-tile';
import JackpotsContainer from './components/JackpotsContainer/JackpotsContainer';
import styles from './JackpotsWrapper.module.scss';
import {
  OverlayJackpot,
  JackpotStaticTexts,
  WrapperImages,
  WrapperJackpotTiers,
  WrapperSize,
  WrapperType,
} from './types';

export interface JackpotsWrapperPropsInterface {
  /**
   * Wrapper Type
   */
  type: WrapperType;
  /**
   * Wrapper Size - scaling of the wrapper
   * @default 'slim'
   */
  size?: WrapperSize;
  /**
   * Currency Code - used for formatting jackpot value and retrieving currency value
   * @default 'GBP'
   */
  currencyCode?: string;
  /**
   * Locale - currently used for formatting jackpot value
   * Check `Number.toLocaleString` for more information
   * @default 'en-GB'
   */
  locale?: string;
  /**
   * Images - must contain logo image and can contain background image
   * Supports retina, but does not have to be provided
   * Values must be presented as URLs to the image!
   */
  images: WrapperImages;
  /**
   * Tiers - list of jackpots that should be presented on the wrapper
   * Must contain 3 elements
   */
  tiers: WrapperJackpotTiers;
  /**
   * Duration (in milliseconds) of the animation for the value to reach from previous
   * to next (current) value
   * @default 120000
   */
  valueAnimationDuration?: number;
  /**
   * Overlay - tier that will be displayed when passed as won tier as overlay on current jackpot
   */
  overlayTier?: OverlayJackpot;
  /**
   * Flag to decide if logo should scale down depending on screen size or always be big
   * @default false
   */
  scaleLogo?: boolean;
  /**
   * Translated static text for jackpot messages
   */
  staticTexts: JackpotStaticTexts;
}

const getBackground = (
  type: WrapperType,
  images: WrapperImages,
  windowRatio: number
) => {
  const imageBackground = (images.background?.source?.modern ||
    images.background?.source)?.[windowRatio > 1 ? 'retina' : 'normal'];

  if (imageBackground) {
    return {
      className: null,
      style: { backgroundImage: imageBackground },
    };
  }

  return {
    className:
      type === 'red-tiger'
        ? styles.redTigerBackground
        : styles.vegasMillionsBackground,
    style: {},
  };
};

const JackpotsWrapper = ({
  type,
  size = 'slim',
  currencyCode = 'GBP',
  locale = 'en-GB',
  images,
  tiers,
  valueAnimationDuration = 120000,
  overlayTier,
  scaleLogo = false,
  staticTexts,
}: JackpotsWrapperPropsInterface) => {
  const windowRatio = useDeviceRatio().ratio;

  const {
    className: backgroundClassName,
    style: backgroundStyle,
  } = getBackground(type, images, windowRatio);

  return (
    <div
      className={cx(styles.container, backgroundClassName)}
      style={backgroundStyle}
      data-testid="jackpots-wrapper"
    >
      <div className={cx(styles.image)} data-testid="jackpots-wrapper-image">
        <LayersWrapper>
          <AssetLayer
            imageSrc={
              images.logo
                ? {
                    normal: images.logo.source.modern
                      ? images.logo.source.modern.normal
                      : images.logo.source.normal,
                    retina: images.logo.source.modern
                      ? images.logo.source.modern?.retina
                      : images.logo.source?.retina,
                  }
                : undefined
            }
            imageAttributes={{ style: { maxWidth: '100%' } }}
            alt={images.logo.alt ? images.logo.alt : ''}
            isLazy={true}
            type="image"
            fallbackBackgroundColor="transparent"
          />
        </LayersWrapper>
      </div>
      <JackpotsContainer
        overlayTier={overlayTier}
        wrapperSize={size}
        wrapperType={type}
        currencyCode={currencyCode}
        locale={locale}
        tiers={tiers}
        valueAnimationDuration={valueAnimationDuration}
        scaleLogo={scaleLogo}
        staticTexts={staticTexts}
      />
    </div>
  );
};

export default JackpotsWrapper;
